// src/views/AccueilLanding.jsx
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import './scss/accueil-landing.scss';

import VideoGallery from './components/VideoGallery'; // ⬅️ existe déjà

/* ---------- TILT / PARALLAX ---------- */
function usePrefersReducedMotion() {
    const [prefers, setPrefers] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setPrefers(mq.matches);
        onChange();
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);
    return prefers;
}

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Tilt
 * - trigger="global"  : réagit à la souris partout (mousemove window) + orientation mobile
 * - trigger="hover"   : réagit seulement quand la souris est au-dessus de l’élément
 * - Inertie via spring
 */
function Tilt({
    children,
    className = '',
    maxRotate = 10,
    maxTranslate = 10,
    perspective = 800,
    spring = 0.18,
    disabled = false,
    depthChild = true,
    trigger = 'global', // 'global' | 'hover'
    ...rest
}) {
    const prefersReduced = usePrefersReducedMotion();
    const ref = useRef(null);
    const stateRef = useRef({
        rx: 0, ry: 0, tx: 0, ty: 0,
        targetRx: 0, targetRy: 0, targetTx: 0, targetTy: 0,
        raf: null,
    });

    const animate = useCallback(() => {
        const st = stateRef.current;
        const lerp = (a, b) => a + (b - a) * spring;
        st.rx = lerp(st.rx, st.targetRx);
        st.ry = lerp(st.ry, st.targetRy);
        st.tx = lerp(st.tx, st.targetTx);
        st.ty = lerp(st.ty, st.targetTy);

        if (ref.current) {
            ref.current.style.setProperty('--rx', st.rx.toFixed(3));
            ref.current.style.setProperty('--ry', st.ry.toFixed(3));
            ref.current.style.setProperty('--tx', st.tx.toFixed(3));
            ref.current.style.setProperty('--ty', st.ty.toFixed(3));
            ref.current.style.setProperty('--persp', `${perspective}px`);
        }
        st.raf = requestAnimationFrame(animate);
    }, [perspective, spring]);

    const setTargetFromPoint = useCallback((clientX, clientY) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clamp((clientX - cx) / (rect.width / 2), -1, 1);
        const dy = clamp((clientY - cy) / (rect.height / 2), -1, 1);

        const st = stateRef.current;
        st.targetRy = dx * maxRotate;      // rotateY
        st.targetRx = -dy * maxRotate;     // rotateX
        st.targetTx = dx * maxTranslate;   // translateX
        st.targetTy = dy * maxTranslate;   // translateY
    }, [maxRotate, maxTranslate]);

    // Mode "global": souris partout dans la page
    useEffect(() => {
        if (disabled || prefersReduced || trigger !== 'global') return;
        const mq = window.matchMedia('(pointer: fine)');
        const enableMouse = mq.matches;

        const onMove = (e) => setTargetFromPoint(e.clientX, e.clientY);
        const onLeaveWindow = () => {
            const st = stateRef.current;
            st.targetRx = 0; st.targetRy = 0; st.targetTx = 0; st.targetTy = 0;
        };

        if (enableMouse) {
            window.addEventListener('mousemove', onMove, { passive: true });
            window.addEventListener('mouseleave', onLeaveWindow, { passive: true });
        }
        return () => {
            if (enableMouse) {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseleave', onLeaveWindow);
            }
        };
    }, [disabled, prefersReduced, trigger, setTargetFromPoint]);

    // Mode "hover": souris seulement au-dessus
    useEffect(() => {
        if (disabled || prefersReduced || trigger !== 'hover') return;

        const el = ref.current;
        if (!el) return;

        const mq = window.matchMedia('(pointer: fine)');
        if (!mq.matches) return;

        const onMove = (e) => setTargetFromPoint(e.clientX, e.clientY);
        const onLeave = () => {
            const st = stateRef.current;
            st.targetRx = 0; st.targetRy = 0; st.targetTx = 0; st.targetTy = 0;
        };

        el.addEventListener('mousemove', onMove, { passive: true });
        el.addEventListener('mouseleave', onLeave, { passive: true });

        return () => {
            el.removeEventListener('mousemove', onMove);
            el.removeEventListener('mouseleave', onLeave);
        };
    }, [disabled, prefersReduced, trigger, setTargetFromPoint]);

    // Orientation (mobile) — trigger="global"
    useEffect(() => {
        if (disabled || prefersReduced || trigger !== 'global') return;
        const handler = (e) => {
            const nb = clamp((e.beta || 0) / 45, -1, 1);
            const ng = clamp((e.gamma || 0) / 45, -1, 1);
            const st = stateRef.current;
            st.targetRy = ng * maxRotate * 0.5;
            st.targetRx = -nb * maxRotate * 0.5;
            st.targetTx = ng * maxTranslate * 0.4;
            st.targetTy = nb * maxTranslate * 0.4;
        };
        window.addEventListener('deviceorientation', handler, { passive: true });
        return () => window.removeEventListener('deviceorientation', handler);
    }, [disabled, prefersReduced, trigger, maxRotate, maxTranslate]);

    useEffect(() => {
        if (disabled || prefersReduced) return;
        stateRef.current.raf = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(stateRef.current.raf);
    }, [animate, disabled, prefersReduced]);

    const mergedClass = `rl-tilt ${depthChild ? 'rl-tilt--depth' : ''} ${className || ''}`;

    return (
        <div ref={ref} className={mergedClass} {...rest}>
            {children}
        </div>
    );
}

/* ---------- Helpers de typage/tri ---------- */
const ALL = 'all';
const TYPES = ['Melee', 'Range', 'Mage'];

// 🔢 Page sizes (modifiable)
const CHAR_PAGE_SIZE = 6;
const SPIR_PAGE_SIZE = 6;

function normalizeTypeTags(typeString) {
    if (!typeString) return [];
    return typeString.split('/').map(s => s.trim()).map(s => {
        const low = s.toLowerCase();
        if (low.startsWith('mage')) return 'Mage';
        if (low.startsWith('range')) return 'Range';
        if (low.startsWith('melee') || low.startsWith('mêlée')) return 'Melee';
        return s;
    });
}

function bySortKey(sortKey) {
    switch (sortKey) {
        case 'name-asc': return (a, b) => a.name.localeCompare(b.name);
        case 'name-desc': return (a, b) => b.name.localeCompare(a.name);
        case 'diff-asc': return (a, b) => (a.difficulty ?? 0) - (b.difficulty ?? 0) || a.name.localeCompare(b.name);
        case 'diff-desc': return (a, b) => (b.difficulty ?? 0) - (a.difficulty ?? 0) || a.name.localeCompare(b.name);
        default: return () => 0;
    }
}

/* ---------- Steam news helpers (decode + sanitize) ---------- */
const STEAM_CLAN_IMAGE = 'https://clan.fastly.steamstatic.com/images';

function decodeHtmlEntities(str) {
    if (!str) return '';
    try {
        const txt = document.createElement('textarea');
        txt.innerHTML = str
            .replace(/\\\[/g, "[")
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&#91;/g, '[')
            .replace(/&#93;/g, ']')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
        const decoded = txt.value;
        return decoded;
    } catch {
        return str;
    }
}

// Convert a minimal subset of Steam BBCode-ish tags to HTML
function steamBbcodeToHtml(input) {
    if (!input) return '';
    let s = input;

    // artefacts [/*] / [*] -> remove
    s = s.replace(/\[\*\/\]/g, '').replace(/\[\*\]/g, '');
    s = s.replace(/\[\/\*\]/g, '');

    // [url=...]text[/url]
    s = s.replace(/\[url=(.*?)\](.*?)\[\/url\]/gi, (_m, href, text) => {
        try {
            const u = new URL(href, window.location.origin);
            return `<a href="${u.href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        } catch {
            return text;
        }
    });

    // [img src="..."][/img]  |  [img]...[/img]
    s = s.replace(/\[img\s+src="([^"]+)"\]\s*\[\/img\]/gi, (_m, src) =>
        `<img src="${src.replace('{STEAM_CLAN_IMAGE}', STEAM_CLAN_IMAGE)}" alt="">`
    );
    s = s.replace(/\[img\]\s*([^\]]+)\s*\[\/img\]/gi, (_m, src) =>
        `<img src="${src.replace('{STEAM_CLAN_IMAGE}', STEAM_CLAN_IMAGE)}" alt="">`
    );

    // Newlines -> <br>
    s = s.replace(/\r?\n/g, '<br>');
    return s;
}

// Safe-ish sanitizer (allow-list tags/attrs)
function sanitizeHtml(dirty) {
    const allowedTags = new Set([
        'p', 'br', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u', 'img', 'a', 'h1', 'h2', 'h3', 'h4'
    ]);
    const allowedAttrs = { 'a': ['href', 'target', 'rel'], 'img': ['src', 'alt'] };

    const tpl = document.createElement('template');
    tpl.innerHTML = dirty;

    const walk = (node) => {
        if (node.nodeType === 8) { node.remove(); return; }
        if (node.nodeType === 1) {
            const tag = node.tagName.toLowerCase();
            if (!allowedTags.has(tag)) {
                const parent = node.parentNode;
                while (node.firstChild) parent.insertBefore(node.firstChild, node);
                parent.removeChild(node);
                return;
            }
            [...node.attributes].forEach(attr => {
                const name = attr.name.toLowerCase();
                const value = attr.value;
                const okList = allowedAttrs[tag] || [];
                if (!okList.includes(name)) {
                    node.removeAttribute(name);
                } else if (name === 'href' || name === 'src') {
                    const low = value.trim().toLowerCase();
                    const isImgData = tag === 'img' && low.startsWith('data:image/');
                    if (low.startsWith('javascript:') || low.startsWith('vbscript:') || (low.startsWith('data:') && !isImgData)) {
                        node.removeAttribute(name);
                    } else if (tag === 'a' && name === 'href') {
                        node.setAttribute('target', '_blank');
                        node.setAttribute('rel', 'noopener noreferrer');
                    }
                }
            });
        }
        [...node.childNodes].forEach(walk);
    };

    [...tpl.content.childNodes].forEach(walk);
    return tpl.innerHTML;
}

function renderSteamHtml(rawHtml) {
    const decoded = decodeHtmlEntities(rawHtml);
    const bb = steamBbcodeToHtml(decoded);
    return sanitizeHtml(bb);
}

/* ---------- Barre de filtres/tri réutilisable ---------- */
function FilterBar({
    title = 'Filters',
    typeFilter, setTypeFilter,
    diffFilter, setDiffFilter,
    sortKey, setSortKey,
    onClear
}) {
    return (
        <div className="rl-filterbar" role="group" aria-label={title}>
            <div className="rl-filterbar__row">
                <label className="rl-field">
                    <span className="rl-field__label">Type</span>
                    <select
                        className="rl-selectinput"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        aria-label="Filter by type"
                    >
                        <option value={ALL}>All types</option>
                        {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </label>

                <label className="rl-field">
                    <span className="rl-field__label">Difficulty</span>
                    <select
                        className="rl-selectinput"
                        value={String(diffFilter)}
                        onChange={(e) => setDiffFilter(e.target.value === ALL ? ALL : Number(e.target.value))}
                        aria-label="Filter by difficulty"
                    >
                        <option value={ALL}>All</option>
                        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}★</option>)}
                    </select>
                </label>

                <label className="rl-field">
                    <span className="rl-field__label">Sort</span>
                    <select
                        className="rl-selectinput"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        aria-label="Sort items"
                    >
                        <option value="relevance">Default</option>
                        <option value="name-asc">Name ↑</option>
                        <option value="name-desc">Name ↓</option>
                        <option value="diff-asc">Difficulty ↑</option>
                        <option value="diff-desc">Difficulty ↓</option>
                    </select>
                </label>

                <button type="button" className="rl-btn rl-btn--ghost rl-filterbar__clear" onClick={onClear}>
                    Reset
                </button>
            </div>
        </div>
    );
}

/* ---------- Pagination réutilisable ---------- */
function Pager({ page, totalPages, onChange }) {
    if (totalPages <= 1) return null;

    const go = (p) => {
        const np = Math.min(Math.max(1, p), totalPages);
        if (np !== page) onChange(np);
    };

    // Crée une petite fenêtre de pages (1 … n)
    const nums = [];
    const span = 3;
    const start = Math.max(1, page - span);
    const end = Math.min(totalPages, page + span);
    for (let i = start; i <= end; i++) nums.push(i);

    return (
        <nav className="rl-pager" aria-label="Pagination">
            <button className="rl-pager__btn" onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Previous page">‹</button>
            <div className="rl-pager__nums" role="list">
                {start > 1 && (
                    <>
                        <button className="rl-pager__num" onClick={() => go(1)} aria-current={page === 1 ? 'page' : undefined}>1</button>
                        {start > 2 && <span className="rl-pager__dots" aria-hidden="true">…</span>}
                    </>
                )}
                {nums.map(n => (
                    <button
                        key={n}
                        className="rl-pager__num"
                        onClick={() => go(n)}
                        aria-current={page === n ? 'page' : undefined}
                    >
                        {n}
                    </button>
                ))}
                {end < totalPages && (
                    <>
                        {end < totalPages - 1 && <span className="rl-pager__dots" aria-hidden="true">…</span>}
                        <button className="rl-pager__num" onClick={() => go(totalPages)} aria-current={page === totalPages ? 'page' : undefined}>{totalPages}</button>
                    </>
                )}
            </div>
            <button className="rl-pager__btn" onClick={() => go(page + 1)} disabled={page >= totalPages} aria-label="Next page">›</button>
        </nav>
    );
}

/* ---------- Composants News (cartes + popup) ---------- */
function NewsCard({ item, onOpen }) {
    const dateStr = new Date(item.date * 1000).toLocaleDateString(navigator.language || 'fr-FR', {
        year: 'numeric', month: 'short', day: '2-digit'
    });
    const title = decodeHtmlEntities(item.title || 'News');

    const raw = item.html || item.excerptHtml || item.snippet || '';
    const safeHtml = renderSteamHtml(raw);
    const m = safeHtml.match(/<img[^>]+src="([^"]+)"/i);
    const cover = m ? m[1] : null;

    return (
        <article
            className="rl-news__card"
            onClick={() => onOpen(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(item)}
        >
            {cover && (
                <div className="rl-news__cover">
                    <img src={cover} alt="" />
                </div>
            )}
            <div className="rl-news__meta">
                <span className="rl-news__author">{item.author || 'Steam'}</span>
                <span className="rl-news__dot">•</span>
                <time className="rl-news__date" dateTime={new Date(item.date * 1000).toISOString()}>
                    {dateStr}
                </time>
            </div>
            <h3 className="rl-news__title">{title}</h3>
        </article>
    );
}

function NewsModal({ item, onClose, onPrev, onNext }) {
    const escClose = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
    useEffect(() => {
        document.addEventListener('keydown', escClose);
        return () => document.removeEventListener('keydown', escClose);
    }, [escClose]);

    if (!item) return null;

    const dateStr = new Date(item.date * 1000).toLocaleDateString(navigator.language || 'fr-FR', {
        year: 'numeric', month: 'long', day: '2-digit'
    });
    const raw = item.html || item.excerptHtml || item.snippet || '';
    const body = renderSteamHtml(raw);
    console.log(body);
    const m = body.match(/<img[^>]+src="([^"]+)"/i);
    const banner = m ? m[1] : null;

    return (
        <div className="rl-newsmodal" role="dialog" aria-modal="true" aria-label="Patchnote">
            <div className="rl-newsmodal__scrim" onClick={onClose} />
            <div className="rl-newsmodal__panel">
                <button className="rl-newsmodal__close" onClick={onClose} aria-label="Close">×</button>

                {banner && (
                    <div className="rl-newsmodal__banner">
                        <img src={banner} alt="" />
                    </div>
                )}

                <header className="rl-newsmodal__header">
                    <div className="rl-newsmodal__meta">
                        <span className="rl-news__author">{item.author || 'Steam'}</span>
                        <span className="rl-news__dot">•</span>
                        <time className="rl-news__date" dateTime={new Date(item.date * 1000).toISOString()}>{dateStr}</time>
                    </div>
                    <h3 className="rl-newsmodal__title">{decodeHtmlEntities(item.title || 'News')}</h3>
                </header>

                <div className="rl-newsmodal__body" dangerouslySetInnerHTML={{ __html: body }} />

                <footer className="rl-newsmodal__footer">
                    <a className="rl-btn rl-btn--ghost" href={item.url} target="_blank" rel="noopener noreferrer">Voir sur Steam</a>
                    <div className="rl-newsmodal__nav">
                        <button type="button" className="rl-btn rl-btn--ghost" onClick={onPrev} aria-label="Previous">◀</button>
                        <button type="button" className="rl-btn rl-btn--ghost" onClick={onNext} aria-label="Next">▶</button>
                    </div>
                </footer>
            </div>
        </div>
    );
}

function NewsSection({ appid = 2713480, count = 3 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openIndex, setOpenIndex] = useState(-1);

    useEffect(() => {
        let cancelled = false;
        async function run() {
            try {
                setLoading(true);
                setError(null);
                const r = await fetch(
                    `https://rivandy.com:3003/steam/news?appid=${appid}&count=${count}&maxlength=0&feeds=steam_community_announcements`,
                    { credentials: 'omit', mode: 'cors' }
                );
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const json = await r.json();
                if (!cancelled) setItems(Array.isArray(json.items) ? json.items : []);
            } catch (e) {
                if (!cancelled) setError(e.message || String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        run();
        return () => { cancelled = true; };
    }, [appid, count]);

    const onOpen = (item) => setOpenIndex(items.findIndex(it => (it.id || it.title) === (item.id || item.title)));
    const onClose = () => setOpenIndex(-1);
    const onPrev = () => setOpenIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    const onNext = () => setOpenIndex((i) => (i >= items.length - 1 ? 0 : i + 1));

    return (
        <section id="news" className="rl-news">
            <div className="rl-news__inner">
                <h2 className="rl-news__title">LATEST NEWS</h2>

                {loading && (
                    <div className="rl-news__state" aria-live="polite">Loading news…</div>
                )}

                {error && (
                    <div className="rl-news__state rl-news__state--error">
                        Failed to load news: {error}
                    </div>
                )}

                {!loading && !error && (!items || items.length === 0) && (
                    <div className="rl-news__state">No announcements yet.</div>
                )}

                <div className="rl-news__grid">
                    {items?.map((it) => (
                        <NewsCard key={it.id || `${it.date}-${it.title}`} item={it} onOpen={onOpen} />
                    ))}
                </div>

                {openIndex >= 0 && items[openIndex] && (
                    <NewsModal
                        item={items[openIndex]}
                        onClose={onClose}
                        onPrev={onPrev}
                        onNext={onNext}
                    />
                )}
            </div>
        </section>
    );
}

/* ---------- PAGE ---------- */
function AccueilLanding() {
    const settings = useSelector((state) => state);
    const img = (p) => process.env.PUBLIC_URL + `/img/${p}`;
    const asset = (p) => process.env.PUBLIC_URL + `/img/${p}.png`;

    /* ---------- SPELLS ---------- */
    const spells = useMemo(() => ([
        { id: 0, name: 'Dragon Charge', description: 'Charge forward with speed and stun all enemies hit.', icon: 'spells/1' },
        { id: 1, name: 'Tornado', description: 'Spin with force and lift your enemies into the air.', icon: 'spells/2' },
        { id: 2, name: 'Shield', description: 'Protects the caster with 50 health points for a short time.', icon: 'spells/3' },
        { id: 3, name: 'Sentence', description: 'Charge forward and stun enemies around the impact zone.', icon: 'spells/4' },
        { id: 4, name: 'Instant', description: 'A violent, fast dash dealing damage.', icon: 'spells/16' },
        { id: 5, name: 'Sword Lift', description: 'Lift your opponents into the air while dealing damage.', icon: 'spells/17' },
        { id: 6, name: 'Blade Dance', description: 'Spin around and keep your opponents airborne.', icon: 'spells/18' },
        { id: 7, name: 'Final Breeze', description: 'Deals heavy damage and knocks your opponents to the ground.', icon: 'spells/19' },
        { id: 8, name: 'Lightning', description: 'Summons a small lightning circle that deals damage.', icon: 'spells/6' },
        { id: 9, name: 'Thunder', description: 'Summons thunder that stuns and deals damage.', icon: 'spells/7' },
        { id: 10, name: 'Celestial Wind', description: 'Summons a wind that pushes and deals damage.', icon: 'spells/8' },
        { id: 11, name: 'Storm', description: 'Summons lightning bolts that deal heavy damage.', icon: 'spells/9' },
        { id: 12, name: 'Phoenix', description: 'Phoenixes crashing in front of you.', icon: 'spells/11' },
        { id: 13, name: 'Falling Phoenix', description: 'Phoenixes soar down and lift enemies.', icon: 'spells/12' },
        { id: 14, name: 'Electric Arrow', description: 'A paralyzing arrow.', icon: 'spells/13' },
        { id: 15, name: 'Rain of Arrows', description: 'Arrows fall from the sky.', icon: 'spells/14' },
        { id: 16, name: 'Hammer of Justice', description: 'A gigantic axe from the sky stuns nearby enemies.', icon: 'spells/21' },
        { id: 17, name: 'Quake', description: 'The ground shakes, dealing damage.', icon: 'spells/22' },
        { id: 18, name: 'Hammer Throw', description: 'A spinning hammer that stuns and deals damage.', icon: 'spells/23' },
        { id: 19, name: 'Death’s Grip', description: 'Devastating shock from the gauntlet.', icon: 'spells/24' },
        { id: 20, name: 'Water Beam', description: 'Pushes and deals damage.', icon: 'spells/26' },
        { id: 21, name: 'Bubble Line', description: 'A straight line of bubbles.', icon: 'spells/27' },
        { id: 22, name: 'Bubble Rain', description: 'Shower of bubbles from the sky.', icon: 'spells/28' },
        { id: 23, name: 'Water Wave', description: 'Massive wave lifting enemies.', icon: 'spells/29' },
        { id: 24, name: 'Savage Charge', description: 'Powerful leap, shocking impact.', icon: 'spells/31' },
        { id: 25, name: 'Shockwave', description: 'Stuns all nearby enemies.', icon: 'spells/32' },
        { id: 26, name: 'Converging Blades', description: 'Two blades converge for a slash.', icon: 'spells/33' },
        { id: 27, name: 'Electrifying Mist', description: 'Energy mist dealing damage.', icon: 'spells/34' },
        { id: 28, name: 'Crystals', description: 'Five icy crystals fly forward.', icon: 'spells/36' },
        { id: 29, name: 'Ice Barricade', description: 'Frozen barrier of spikes.', icon: 'spells/37' },
        { id: 30, name: 'Freezing Orb', description: 'Immobilizes and deals damage.', icon: 'spells/38' },
        { id: 31, name: 'Forbidden Spell: Ice Storm', description: 'Summons a snowstorm.', icon: 'spells/39' },

        { id: 1000, name: 'Rush', description: 'Headbutt charge.', icon: 'spells/60' },
        { id: 1001, name: 'Spore', description: 'Releases spores.', icon: 'spells/61' },
        { id: 1002, name: 'Floral Protection', description: 'Brief protection.', icon: 'spells/62' },
        { id: 1003, name: 'Floral Instinct', description: 'Pulls nearby enemies.', icon: 'spells/63' },
        { id: 1004, name: 'Shatter', description: 'Body breaks and damages.', icon: 'spells/64' },
        { id: 1005, name: 'Piercing Throw', description: 'Parts charge forward.', icon: 'spells/65' },
        { id: 1006, name: 'Rock Tomb', description: 'Pieces crash down.', icon: 'spells/66' },
        { id: 1007, name: 'Shockwave', description: 'Stuns enemies.', icon: 'spells/67' },
        { id: 1008, name: 'Thorns', description: 'Ground thorns rush forward.', icon: 'spells/68' },
        { id: 1009, name: 'Ancestral Rune', description: 'Reduces movement/jumps.', icon: 'spells/69' },
        { id: 1010, name: 'Natural Retreat', description: 'Protective barrier.', icon: 'spells/70' },
        { id: 1011, name: 'Giant Thorn', description: 'Giant thorn erupts.', icon: 'spells/71' },
        { id: 1012, name: 'Phantom Leap', description: 'Launch yourself forward.', icon: 'spells/40' },
        { id: 1013, name: 'Curse', description: 'Cursed moving zone.', icon: 'spells/41' },
        { id: 1014, name: 'Fog', description: 'Conceal yourself.', icon: 'spells/42' },
        { id: 1015, name: 'Spectral Explosion', description: 'Spectral energy burst.', icon: 'spells/43' },
        { id: 1016, name: 'Infernal Flames', description: 'Fire cone burns.', icon: 'spells/44' },
        { id: 1017, name: 'Roll', description: 'Roll forward faster.', icon: 'spells/45' },
        { id: 1018, name: 'Deadly Curse', description: 'Demonic spherical zone.', icon: 'spells/46' },
        { id: 1019, name: 'Chaotic Explosion', description: 'Devastating energy.', icon: 'spells/47' },
        { id: 1020, name: 'Ascension', description: 'Surge forward larger.', icon: 'spells/48' },
        { id: 1021, name: 'Call of the Sisters', description: 'Two larvae charge.', icon: 'spells/49' },
        { id: 1022, name: 'Scream', description: 'Cone stun.', icon: 'spells/50' },
        { id: 1023, name: 'Call of the Queen', description: 'Queen Larva deals colossal damage.', icon: 'spells/51' },
        { id: 1024, name: 'Flight', description: 'Opens and flies briefly.', icon: 'spells/52' },
        { id: 1025, name: 'Chomp, Chomp!', description: 'Crawls and chomps.', icon: 'spells/53' },
        { id: 1026, name: 'Leaf Spit', description: 'Rain of leaves forward.', icon: 'spells/54' },
        { id: 1027, name: 'Fall, Book!', description: 'Falls to bite.', icon: 'spells/55' },
        { id: 1028, name: 'Extending Leap', description: 'Leap forward.', icon: 'spells/56' },
        { id: 1029, name: 'Pulverizing Strike', description: 'Hurls parts forward.', icon: 'spells/57' },
        { id: 1030, name: 'Cell Division', description: 'Pieces hurt on contact.', icon: 'spells/58' },
        { id: 1031, name: 'Absorption', description: 'Swallows enemies.', icon: 'spells/59' },
        { id: 1032, name: 'Fissure', description: 'Two-stage fissure blast.', icon: 'spells/71' },
        { id: 1033, name: 'Dark Assault', description: 'Dash forward, damage.', icon: 'spells/72' },
        { id: 1034, name: 'Attraction', description: 'Pulls enemies in.', icon: 'spells/73' },
        { id: 1035, name: 'Anti-Magic Vortex', description: 'Destroys enemy spells.', icon: 'spells/74' },

        // Mafalda
        { id: 1100, name: 'Minefield', description: 'Deploys a field of mines that detonate on contact.', icon: 'spells/80' },
        { id: 1101, name: 'Stun Bomb', description: 'Throws a bomb that stuns and damages enemies in an area.', icon: 'spells/81' },
        { id: 1102, name: 'Repulsive Grenade', description: 'Detonates and pushes enemies away while dealing damage.', icon: 'spells/82' },
        { id: 1103, name: 'Missile Rain', description: 'Launches a barrage of missiles raining on the target area.', icon: 'spells/83' },
    ]), []);
    const spellById = useMemo(() => new Map(spells.map(s => [s.id, s])), [spells]);

    /* ---------- Characters ---------- */
    const characters = useMemo(() => ([
        { id: 0, name: 'Niklaus', description: 'Niklaus is the newest loyal knight...', icon: 'characters/niklaus', fullIcon: 'characters/niklaus', characterType: 'Melee', difficulty: 1, spellIds: [0, 1, 2, 3] },
        { id: 1, name: 'Thora', description: 'Thora, daughter of Makani Varsha...', icon: 'characters/lycia', fullIcon: 'characters/lycia', characterType: 'Mage / Range', difficulty: 1, spellIds: [8, 9, 10, 11] },
        { id: 2, name: 'Kimka', description: 'Born in the slums of Polemos...', icon: 'characters/kimka', fullIcon: 'characters/kimka', characterType: 'Range', difficulty: 1, spellIds: [12, 13, 14, 15] },
        { id: 3, name: 'Kemo', description: 'Trained in the voice of the breath.', icon: 'characters/kemo', fullIcon: 'characters/kemo', characterType: 'Melee', difficulty: 3, spellIds: [4, 5, 6, 7] },
        { id: 4, name: 'Elza', description: 'Bearer of the corrupt spirit Korn.', icon: 'characters/elza', fullIcon: 'characters/elza', characterType: 'Mage / Range', difficulty: 2, spellIds: [20, 21, 22, 23] },
        { id: 5, name: 'Dimitri', description: 'Center of attention turned mercenary.', icon: 'characters/serbas', fullIcon: 'characters/serbas', characterType: 'Melee', difficulty: 4, spellIds: [24, 25, 26, 27] },
        { id: 6, name: 'Guldir', description: 'Forged at the anvil of Trabouc.', icon: 'characters/guldir', fullIcon: 'characters/guldir', characterType: 'Melee', difficulty: 2, spellIds: [16, 17, 18, 19] },
        { id: 7, name: 'Jasper', description: 'A brilliant mage searching his twin.', icon: 'characters/jasper', fullIcon: 'characters/jasper', characterType: 'Mage / Range', difficulty: 4, spellIds: [28, 29, 30, 31] },
        { id: 8, name: 'Kyren', description: 'A fearless warrior fighting for honor.', icon: 'characters/kyren', fullIcon: 'characters/kyren', characterType: 'Melee', difficulty: 5, spellIds: [1032, 1033, 1034, 1035] },

        // Mafalda
        { id: 9, name: 'Mafalda', description: 'A tactical ranged specialist using explosives and area control.', icon: 'characters/mafalda', fullIcon: 'characters/mafalda', characterType: 'Range', difficulty: 3, spellIds: [1100, 1101, 1102, 1103] },
    ]), []);

    /* ---------- Spirits ---------- */
    const spirits = useMemo(() => ([
        { id: 0, name: 'Flochon', description: 'Fast and agile—never trust appearances.', icon: 'characters/flochon', fullIcon: 'characters/flochon', characterType: 'Melee / Range', difficulty: 2, spellIds: [1000, 1001, 1002, 1003] },
        { id: 1, name: 'Golem', description: 'Patient and powerful—wait for the right moment.', icon: 'characters/golem', fullIcon: 'characters/golem', characterType: 'Melee', difficulty: 5, spellIds: [1004, 1005, 1006, 1007] },
        { id: 3, name: 'Woop', description: 'The forest is your ally.', icon: 'characters/woop', fullIcon: 'characters/woop', characterType: 'Melee / Range', difficulty: 3, spellIds: [1008, 1009, 1010, 1011] },
        { id: 5, name: 'Ghoo', description: 'Emptiness incarnate.', icon: 'characters/ghost', fullIcon: 'characters/ghost', characterType: 'Range', difficulty: 1, spellIds: [1012, 1013, 1014, 1015] },
        { id: 6, name: 'Diaby', description: 'Pure evil—nothing more to add.', icon: 'characters/diaby', fullIcon: 'characters/diaby', characterType: 'Range / Mage', difficulty: 2, spellIds: [1016, 1017, 1018, 1019] },
        { id: 7, name: 'Woom', description: 'A parasitic worm.', icon: 'characters/woom', fullIcon: 'characters/woom', characterType: 'Range', difficulty: 4, spellIds: [1020, 1021, 1022, 1023] },
        { id: 9, name: 'Bok', description: 'Knowledge with a mouth full of BRBR…', icon: 'characters/bok', fullIcon: 'characters/bok', characterType: 'Melee', difficulty: 1, spellIds: [1024, 1025, 1026, 1027] },
        { id: 10, name: 'Slime', description: 'Happy, happy, happy!', icon: 'characters/slime', fullIcon: 'characters/slime', characterType: 'Melee', difficulty: 5, spellIds: [1028, 1029, 1030, 1031] },
    ]), []);

    /* ---------- États filtre ---------- */
    const [selectedCharId, setSelectedCharId] = useState(null);
    const [selectedSpiritId, setSelectedSpiritId] = useState(null);

    // Characters filter/sort state
    const [charTypeFilter, setCharTypeFilter] = useState(ALL);
    const [charDiffFilter, setCharDiffFilter] = useState(ALL);
    const [charSortKey, setCharSortKey] = useState('relevance');

    // Spirits filter/sort state
    const [spiritTypeFilter, setSpiritTypeFilter] = useState(ALL);
    const [spiritDiffFilter, setSpiritDiffFilter] = useState(ALL);
    const [spiritSortKey, setSpiritSortKey] = useState('relevance');

    // 🔢 Pagination states
    const [charPage, setCharPage] = useState(1);
    const [spiritPage, setSpiritPage] = useState(1);

    // Reset page quand filtres changent
    useEffect(() => { setCharPage(1); }, [charTypeFilter, charDiffFilter, charSortKey, selectedCharId]);
    useEffect(() => { setSpiritPage(1); }, [spiritTypeFilter, spiritDiffFilter, spiritSortKey, selectedSpiritId]);

    /* ---------- UI helpers ---------- */
    const DifficultyStars = ({ n }) => {
        const empty = asset('star');
        const full = asset('star-fill');
        return (
            <span className="rl-stars" aria-label={`Difficulty ${n}/5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <img key={i} src={i < n ? full : empty} alt="" aria-hidden="true" />
                ))}
            </span>
        );
    };

    const IconButton = ({ active, label, src, onClick }) => (
        <button
            type="button"
            className="rl-iconbtn"
            aria-pressed={active ? 'true' : 'false'}
            onClick={onClick}
            title={label}
        >
            <span className="rl-iconbtn__img" data-layer="2"><img src={src} alt="" aria-hidden="true" /></span>
            <span className="rl-iconbtn__label" data-layer="1">{label}</span>
        </button>
    );

    const SpellBar = ({ ids }) => {
        const onToggle = (e) => {
            if (e.currentTarget.open) {
                const parent = e.currentTarget.parentElement;
                parent.querySelectorAll('details.rl-spell[open]').forEach(d => {
                    if (d !== e.currentTarget) d.removeAttribute('open');
                });
            }
        };
        return (
            <div className="rl-spellbar" role="list">
                {ids.map((id, idx) => {
                    const sp = spellById.get(id);
                    if (!sp) return null;
                    const edgeClass = idx === 0 ? ' rl-spell--edge-left'
                        : idx === ids.length - 1 ? ' rl-spell--edge-right' : '';
                    return (
                        <details key={id} className={`rl-spell${edgeClass}`} role="listitem" onToggle={onToggle}>
                            <summary className="rl-spell__thumb" aria-label={`${sp.name}: ${sp.description}`}>
                                <img src={asset(sp.icon)} alt="" aria-hidden="true" />
                            </summary>
                            <div className="rl-spell__panel" role="dialog" aria-label={sp.name}>
                                <div className="rl-spell__row">
                                    <div className="rl-spell__big"><img src={asset(sp.icon)} alt="" aria-hidden="true" /></div>
                                    <div>
                                        <h4 className="rl-spell__title">{sp.name}</h4>
                                        <p className="rl-spell__desc">{sp.description}</p>
                                    </div>
                                </div>
                            </div>
                        </details>
                    );
                })}
            </div>
        );
    };

    // Card : trigger="hover" pour characters & spirits
    const Card = ({ title, text, cover, type, difficulty, spellIds, showMeta = true, hoverOnly = false }) => (
        <Tilt className="rl-card-tilt" maxRotate={8} maxTranslate={8} trigger={hoverOnly ? 'hover' : 'global'}>
            <article className="rl-card">
                <div className="rl-card__media" data-layer="3"><img src={cover} alt="" /></div>
                <div className="rl-card__body" data-layer="1">
                    <h3 className="rl-card__title">{title}</h3>
                    <p className="rl-card__text">{text}</p>

                    {showMeta && (
                        <div className="rl-badges">
                            {type ? <span className="rl-badge">{type}</span> : null}
                            {(typeof difficulty === 'number') ? (
                                <span className="rl-badge rl-badge--stars" title={`Difficulty ${difficulty}/5`}>
                                    <span>Difficulty</span>
                                    <DifficultyStars n={difficulty} />
                                </span>
                            ) : null}
                        </div>
                    )}

                    {Array.isArray(spellIds) && spellIds.length > 0 ? (
                        <SpellBar ids={spellIds} />
                    ) : null}
                </div>
            </article>
        </Tilt>
    );

    /* ---------- Filtres + tris appliqués ---------- */
    const applyFiltersAndSort = (list, { typeFilter, diffFilter, sortKey }) => {
        let arr = list;
        if (typeFilter !== ALL) {
            arr = arr.filter(item => normalizeTypeTags(item.characterType).includes(typeFilter));
        }
        if (diffFilter !== ALL) {
            arr = arr.filter(item => (item.difficulty ?? 0) === diffFilter);
        }
        const sorter = bySortKey(sortKey);
        if (sorter !== bySortKey('relevance')) {
            arr = [...arr].sort(sorter);
        }
        return arr;
    };

    // Base après sélection par icône
    const baseCharacters = selectedCharId == null ? characters : characters.filter(c => c.id === selectedCharId);
    const baseSpirits = selectedSpiritId == null ? spirits : spirits.filter(s => s.id === selectedSpiritId);

    const filteredCharacters = applyFiltersAndSort(baseCharacters, {
        typeFilter: charTypeFilter, diffFilter: charDiffFilter, sortKey: charSortKey
    });

    const filteredSpirits = applyFiltersAndSort(baseSpirits, {
        typeFilter: spiritTypeFilter, diffFilter: spiritDiffFilter, sortKey: spiritSortKey
    });

    // 🔢 Pagination helpers
    const charTotalPages = Math.max(1, Math.ceil(filteredCharacters.length / CHAR_PAGE_SIZE));
    const spiritTotalPages = Math.max(1, Math.ceil(filteredSpirits.length / SPIR_PAGE_SIZE));

    const paginatedCharacters = filteredCharacters.slice((charPage - 1) * CHAR_PAGE_SIZE, charPage * CHAR_PAGE_SIZE);
    const paginatedSpirits = filteredSpirits.slice((spiritPage - 1) * SPIR_PAGE_SIZE, spiritPage * SPIR_PAGE_SIZE);

    // Smooth scroll vers le haut de la section quand page change (UX confort)
    const charSectionRef = useRef(null);
    const spiritSectionRef = useRef(null);

    return (
        <main className={settings.factionSelected === 1 ? 'faction-rivandy' : 'faction-dechys'}>
            {/* HERO */}
            <section className="rl-hero">
                <img className="rl-hero__bg" src={img('bg.png')} alt="" aria-hidden="true" />
                <div className="rl-hero__shade" />
                <div className="rl-hero__content">
                    <Tilt className="rl-hero__logoWrap" maxRotate={10} maxTranslate={8}>
                        <img className="rl-hero__logo" data-layer="3" src={img('rivandy.png')} alt="Rivandy" />
                    </Tilt>
                    <p className="rl-hero__tag">A mystical clash for survival and supremacy</p>
                    <div className="rl-hero__cta">
                        <a className="rl-btn rl-btn--primary" target="_blank" href="https://discord.gg/RmubB8KhRW" aria-label="Play now" data-layer="2">PLAY NOW</a>

                        <div className="rl-hero__stores" aria-label="Available on">
                            <img src={img('steam.png')} alt="Steam" />
                            <img src={img('windows.png')} alt="Windows" />
                        </div>
                    </div>
                </div>
                <div className="rl-sepBand" aria-hidden="true">
                    <svg className="rl-sepBand__svg" viewBox="0 0 100 20" preserveAspectRatio="none" role="presentation">
                        <path d="M 0 2 L 16 9 L 25 6 L 20 2 L 50 11 L 88 0 L 84 4 L 88 6 L 100 2 L 100 8 L 88 10 L 91 13 L 81 10 L 49 13 L 25 9 L 16 11 L 0 9 Z" fill="#D9A12D" stroke="none" />
                    </svg>
                </div>
            </section>

            {/* OVERVIEW */}
            <section id="overview" className="rl-overview">
                <div className="rl-overview__inner">
                    <div className="rl-overview__text">
                        <h2 className="rl-title">OVERVIEW</h2>
                        <p><strong>Rivandy</strong> blends <strong>Battle Royale</strong> and cooperative <strong>Survivor</strong>.</p>
                        <p>You control <strong>two entities at once</strong>:</p>
                        <ul>
                            <li><strong>A character</strong></li>
                            <li><strong>A spirit</strong></li>
                        </ul>
                        <p>Master <strong>4 human abilities</strong> and <strong>4 spirit abilities</strong>.</p>
                    </div>
                    <div className="rl-overview__art"><img src={img('fusion.png')} alt="Hero with spirit" /></div>
                </div>
            </section>

            {/* MODES */}
            <section id="modes" className="rl-modes">
                <img className="rl-sectionBg bg3" src={img('bg3.png')} alt="" aria-hidden="true" />

                <h2 className="rl-modes__title">FIGHT IN TWO MODES</h2>
                <div className="rl-modes__grid">
                    <Card
                        title="Battle Royale"
                        text="Climb the ranks, dominate the map, outlast your opponents."
                        cover={img('br-mode.png')}
                        showMeta={false}
                        hoverOnly={false}
                    />
                    <Card
                        title="Survivor"
                        text="Co-op PvE with relentless waves and events. Unlock new spirits."
                        cover={img('survivor-mode.png')}
                        showMeta={false}
                        hoverOnly={false}
                    />
                </div>
            </section>

            <NewsSection appid={2713480} count={3} />
            {/* PERSONNAGES */}
            <section id="characters" className="rl-select rl-select--characters" ref={charSectionRef}>
                <img className="rl-sectionBg bg2" src={img('bg5.png')} alt="" aria-hidden="true" />
                <div className="rl-select__inner">
                    <h2 className="rl-select__title">CHARACTERS</h2>

                    {/* Barre de filtres / tri */}
                    <FilterBar
                        title="Characters filters"
                        typeFilter={charTypeFilter} setTypeFilter={setCharTypeFilter}
                        diffFilter={charDiffFilter} setDiffFilter={setCharDiffFilter}
                        sortKey={charSortKey} setSortKey={setCharSortKey}
                        onClear={() => { setCharTypeFilter(ALL); setCharDiffFilter(ALL); setCharSortKey('relevance'); }}
                    />

                    {/* Icônes de sélection rapide */}
                    <div className="rl-iconbar" role="tablist" aria-label="Characters">
                        {characters.map(c => (
                            <IconButton
                                key={c.id}
                                label={c.name}
                                src={asset(c.icon)}
                                active={selectedCharId === c.id}
                                onClick={() => setSelectedCharId(prev => prev === c.id ? null : c.id)}
                            />
                        ))}
                    </div>

                    {/* Cartes paginées */}
                    <div className="rl-grid">
                        {paginatedCharacters.map(c => (
                            <Card
                                key={c.id}
                                title={c.name}
                                text={c.description}
                                cover={asset(c.fullIcon)}
                                type={c.characterType}
                                difficulty={c.difficulty}
                                spellIds={c.spellIds}
                                showMeta={true}
                                hoverOnly={true}
                            />
                        ))}

                        {/* —— Fillers invisibles pour garder la hauteur de 6 items —— */}
                        {Array.from({ length: Math.max(0, CHAR_PAGE_SIZE - paginatedCharacters.length) }).map((_, i) => (
                            <div className="rl-card-filler" aria-hidden="true" key={`char-filler-${i}`} />
                        ))}
                    </div>

                    {/* Pagination */}
                    <Pager
                        page={charPage}
                        totalPages={charTotalPages}
                        onChange={setCharPage}
                    />
                </div>
            </section>

            {/* ESPRITS */}
            <section id="spirits" className="rl-select rl-select--spirits" ref={spiritSectionRef}>
                <img className="rl-sectionBg bg1" src={img('bg5.png')} alt="" aria-hidden="true" />

                <div className="rl-select__inner">
                    <h2 className="rl-select__title">SPIRITS</h2>

                    {/* Barre de filtres / tri */}
                    <FilterBar
                        title="Spirits filters"
                        typeFilter={spiritTypeFilter} setTypeFilter={setSpiritTypeFilter}
                        diffFilter={spiritDiffFilter} setDiffFilter={setSpiritDiffFilter}
                        sortKey={spiritSortKey} setSortKey={setSpiritSortKey}
                        onClear={() => { setSpiritTypeFilter(ALL); setSpiritDiffFilter(ALL); setSpiritSortKey('relevance'); }}
                    />

                    {/* Icônes */}
                    <div className="rl-iconbar" role="tablist" aria-label="Spirits">
                        {spirits.map(s => (
                            <IconButton
                                key={s.id}
                                label={s.name}
                                src={asset(s.icon)}
                                active={selectedSpiritId === s.id}
                                onClick={() => setSelectedSpiritId(prev => prev === s.id ? null : s.id)}
                            />
                        ))}
                    </div>

                    {/* Cartes paginées */}
                    <div className="rl-grid">
                        {paginatedSpirits.map(s => (
                            <Card
                                key={s.id}
                                title={s.name}
                                text={s.description}
                                cover={asset(s.fullIcon)}
                                type={s.characterType}
                                difficulty={s.difficulty}
                                spellIds={s.spellIds}
                                showMeta={true}
                                hoverOnly={true}
                            />
                        ))}

                        {/* —— Fillers invisibles pour garder la hauteur de 6 items —— */}
                        {Array.from({ length: Math.max(0, SPIR_PAGE_SIZE - paginatedSpirits.length) }).map((_, i) => (
                            <div className="rl-card-filler" aria-hidden="true" key={`spirit-filler-${i}`} />
                        ))}
                    </div>

                    {/* Pagination */}
                    <Pager
                        page={spiritPage}
                        totalPages={spiritTotalPages}
                        onChange={setSpiritPage}
                    />
                </div>
            </section>

            {/* VIDEOS */}
            <VideoGallery
                title="TRAILER & VIDEOS"
                channelUrl="https://www.youtube.com/@RivandyOnline/videos"
                videos={[
                    { url: 'https://www.youtube.com/watch?v=GE7ZX_aK3YY', title: 'Rivandy – Official Trailer', featured: true },
                    { url: 'https://www.youtube.com/watch?v=D4mMjEN1LpQ', title: 'Battle royale - Gameplay' },
                    { url: 'https://www.youtube.com/watch?v=UOtRlrafgNU&t=7s', title: 'Survivor - Gameplay' },
                ]}
            />

            {/* FOOTER */}
            <footer className="rl-footer">
                <div className="rl-footer__inner">
                    <p className="rl-footer__copy">
                        © {new Date().getFullYear()} Rivandy — All rights reserved.
                    </p>
                    <p className="rl-footer__contact">
                        <a href="mailto:contact@rivandy.com">contact@rivandy.com</a>
                    </p>
                </div>
            </footer>
        </main>
    );
}

export default AccueilLanding;
